import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DesktopDateTimePicker } from '@mui/x-date-pickers/DesktopDateTimePicker';
import DirectionsCarFilledRoundedIcon from '@mui/icons-material/DirectionsCarFilledRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import { toast } from 'react-toastify';
import { assetNamesSelector } from '../../features/assets/assetsSlice';
import { distancePeriodsMain, getperiod } from '../../utils/datePeriodCalculator';
import { buildReportMulti, getReportList } from '../../features/datacenter/reportSlice';

const palette = {
    ink: '#102a43',
    text: '#243b53',
    muted: '#627d98',
    line: '#d9e2ec',
    shell: '#f4f7fb',
    accent: '#2563eb',
    accentSoft: '#ebf2ff',
    emerald: '#0f766e',
    emeraldSoft: '#e7f8f5',
    amber: '#c2410c',
    amberSoft: '#fff1eb',
};

const panelSx = {
    p: { xs: 1.75, md: 2 },
    borderRadius: '22px',
    border: `1px solid ${palette.line}`,
    background: 'linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)',
    boxShadow: '0 14px 30px rgba(15, 23, 42, 0.055)',
};

const flatSurfaceSx = {
    ...panelSx,
    p: 0,
    overflow: 'hidden',
};

const fieldSx = {
    '& .MuiInputLabel-root': {
        color: palette.muted,
        fontSize: 12,
        fontWeight: 600,
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: palette.text,
    },
    '& .MuiOutlinedInput-root': {
        borderRadius: '14px',
        backgroundColor: '#fcfdff',
        transition: 'border-color 160ms ease, box-shadow 160ms ease',
        '& fieldset': {
            borderColor: palette.line,
        },
        '&:hover fieldset': {
            borderColor: alpha(palette.accent, 0.55),
        },
        '&.Mui-focused': {
            boxShadow: `0 0 0 4px ${alpha(palette.accent, 0.12)}`,
        },
        '&.Mui-focused fieldset': {
            borderColor: palette.accent,
        },
    },
    '& .MuiOutlinedInput-input': {
        py: 1.2,
        color: palette.ink,
        fontSize: 13,
        fontWeight: 600,
    },
    '& .MuiInputAdornment-root': {
        color: palette.muted,
        fontWeight: 600,
    },
};

const iconBadgeSx = (background, color) => ({
    width: 36,
    height: 36,
    display: 'grid',
    placeItems: 'center',
    borderRadius: '12px',
    backgroundColor: background,
    color,
    flexShrink: 0,
});

const periodOptions = [...distancePeriodsMain, { name: 'Custom Range', value: 'custom' }];

const SectionHeader = ({ icon, title, description, tone = 'accent' }) => {
    const tones = {
        accent: { background: palette.accentSoft, color: palette.accent },
        emerald: { background: palette.emeraldSoft, color: palette.emerald },
        amber: { background: palette.amberSoft, color: palette.amber },
        ink: { background: alpha(palette.ink, 0.08), color: palette.ink },
    };
    const currentTone = tones[tone] || tones.accent;

    return (
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box sx={iconBadgeSx(currentTone.background, currentTone.color)}>{icon}</Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 17, fontWeight: 800, color: palette.ink, lineHeight: 1.15 }}>
                    {title}
                </Typography>
                {description && (
                    <Typography sx={{ marginTop: 0.35, fontSize: 12, color: palette.muted, lineHeight: 1.45 }}>
                        {description}
                    </Typography>
                )}
            </Box>
        </Stack>
    );
};

const SummaryRow = ({ label, value, divider = true }) => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
            py: 0.9,
            borderBottom: divider ? `1px solid ${alpha(palette.line, 0.8)}` : 'none',
        }}
    >
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: palette.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {label}
        </Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: palette.ink, textAlign: 'right' }}>
            {value}
        </Typography>
    </Box>
);

const ToggleCard = ({ checked, onChange, title, description }) => (
    <Paper
        variant="outlined"
        sx={{
            borderRadius: '18px',
            borderColor: checked ? alpha(palette.accent, 0.35) : palette.line,
            backgroundColor: checked ? alpha(palette.accent, 0.06) : '#fbfdff',
            boxShadow: 'none',
        }}
    >
        <FormControlLabel
            sx={{
                margin: 0,
                width: '100%',
                alignItems: 'flex-start',
                padding: 1.15,
                gap: 0.75,
                '& .MuiFormControlLabel-label': {
                    flex: 1,
                    marginTop: 0.1,
                },
            }}
            control={(
                <Checkbox
                    checked={checked}
                    onChange={(e) => onChange(e.target.checked)}
                    size="small"
                    sx={{
                        color: palette.muted,
                        '&.Mui-checked': {
                            color: palette.accent,
                        },
                    }}
                />
            )}
            label={(
                <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: palette.ink }}>
                        {title}
                    </Typography>
                    <Typography sx={{ marginTop: 0.25, fontSize: 11, color: palette.muted, lineHeight: 1.45 }}>
                        {description}
                    </Typography>
                </Box>
            )}
        />
    </Paper>
);

const GenfleetReportForm = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const isAdmin = useSelector((state) => state?.user?.isAdmin);
    const assetNames = useSelector(assetNamesSelector);

    const [vehicleSearch, setVehicleSearch] = useState('');
    const [multiVehicles, setMultiVehicles] = useState([]);
    const [multiAssetIds, setMultiAssetIds] = useState([]);
    const [periodType, setPeriodType] = useState('today');
    const [fromDate, setFromDate] = useState(getperiod('today').fromDate);
    const [toDate, setToDate] = useState(getperiod('today').toDate);
    const [includeEventList, setIncludeEventList] = useState(false);
    const [includeDebug, setIncludeDebug] = useState(false);
    const [forceCrawl, setForceCrawl] = useState(false);
    const [minActive, setMinActive] = useState(5);
    const [hold, setHold] = useState(2);
    const [disableCrawlFallback, setDisableCrawlFallback] = useState(false);

    const assetOptions = useMemo(() => (assetNames ? Object.keys(assetNames) : []), [assetNames]);
    const selectedAssetIds = useMemo(() => multiAssetIds.filter((id) => id !== ''), [multiAssetIds]);
    const selectedCount = selectedAssetIds.length;
    const allVehiclesSelected = assetOptions.length > 0 && selectedCount === assetOptions.length;
    const hasInvalidRange = Boolean(fromDate && toDate && toDate.isSameOrBefore(fromDate));

    const periodLabel = useMemo(() => {
        if (periodType === 'custom') {
            return 'Custom range';
        }
        return periodOptions.find((option) => option.value === periodType)?.name || 'Custom range';
    }, [periodType]);

    useEffect(() => {
        if (open) {
            setVehicleSearch('');
            setMultiVehicles([]);
            setMultiAssetIds([]);
            setPeriodType('today');
            setFromDate(getperiod('today').fromDate);
            setToDate(getperiod('today').toDate);
            setIncludeEventList(false);
            setIncludeDebug(false);
            setForceCrawl(false);
            setMinActive(5);
            setHold(2);
            setDisableCrawlFallback(false);
        }
    }, [open]);

    useEffect(() => {
        if (!assetNames) {
            return;
        }
        const derivedVehicles = selectedAssetIds.map((assetId) => assetNames?.[assetId]?.regn || '');
        const needsSync = derivedVehicles.length !== multiVehicles.length
            || derivedVehicles.some((name, index) => name !== (multiVehicles[index] || ''));

        if (needsSync) {
            setMultiVehicles(derivedVehicles);
        }
    }, [assetNames, multiVehicles, selectedAssetIds]);

    const submitDisabled = useMemo(() => {
        return selectedCount === 0 || !fromDate || !toDate || hasInvalidRange;
    }, [selectedCount, fromDate, toDate, hasInvalidRange]);

    const handlePeriodTypeChange = (event) => {
        const nextPeriodType = event.target.value;
        setPeriodType(nextPeriodType);
        if (nextPeriodType !== 'custom') {
            const { fromDate: nextFromDate, toDate: nextToDate } = getperiod(nextPeriodType);
            setFromDate(nextFromDate);
            setToDate(nextToDate);
        }
    };

    const handleFromDateChange = (value) => {
        setFromDate(value);
        setPeriodType('custom');
    };

    const handleToDateChange = (value) => {
        setToDate(value);
        setPeriodType('custom');
    };

    const handleVehicleAdd = (_, assetId) => {
        if (!assetId || selectedAssetIds.includes(assetId)) {
            setVehicleSearch('');
            return;
        }
        const regn = assetNames?.[assetId]?.regn || assetId;
        setMultiAssetIds((prev) => [...prev, assetId]);
        setMultiVehicles((prev) => [...prev, regn]);
        setVehicleSearch('');
    };

    const handleVehicleDelete = (assetIdToRemove) => {
        const index = multiAssetIds.indexOf(assetIdToRemove);
        if (index === -1) {
            return;
        }
        const nextVehicles = [...multiVehicles];
        const nextAssetIds = [...multiAssetIds];
        nextVehicles.splice(index, 1);
        nextAssetIds.splice(index, 1);
        setMultiVehicles(nextVehicles);
        setMultiAssetIds(nextAssetIds);
    };

    const handleSelectAllVehicles = () => {
        if (allVehiclesSelected) {
            setMultiAssetIds([]);
            setMultiVehicles([]);
            return;
        }
        const nextAssetIds = assetOptions;
        const nextVehicles = nextAssetIds.map((assetId) => assetNames?.[assetId]?.regn || assetId);
        setMultiAssetIds(nextAssetIds);
        setMultiVehicles(nextVehicles);
    };

    const handleSubmit = () => {
        const assetIds = selectedAssetIds;
        if (assetIds.length === 0) {
            toast.error('Please select at least one vehicle.');
            return;
        }
        if (!fromDate || !toDate || toDate.isSameOrBefore(fromDate)) {
            toast.error('The report end time must be after the start time.');
            return;
        }

        const assets = assetIds.map((assetId, index) => assetNames?.[assetId]?.regn || multiVehicles[index] || assetId);
        const reportParams = {
            assetIds,
            assets,
            reportType: { name: 'Generator Fleet', value: 'genfleet', multiVehicle: true },
            timeFrom: fromDate.format('YYYY-MM-DD HH:mm:ss'),
            timeTo: toDate.format('YYYY-MM-DD HH:mm:ss'),
            crawl: forceCrawl,
            minActive: Number(minActive) || 0,
            hold: Number(hold) || 0,
        };

        if (isAdmin) {
            reportParams.includeEventList = includeEventList;
            reportParams.includeDebug = includeDebug;
            reportParams.disableCrawlFallback = disableCrawlFallback;
        }

        dispatch(buildReportMulti(reportParams));
        dispatch(getReportList());
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    width: 'min(1180px, calc(100% - 40px))',
                    maxHeight: 'calc(100% - 32px)',
                    borderRadius: '28px',
                    background: palette.shell,
                    boxShadow: '0 36px 90px rgba(15, 23, 42, 0.18)',
                    overflow: 'hidden',
                },
            }}
        >
            <DialogTitle sx={{ padding: 0, background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)', borderBottom: `1px solid ${palette.line}` }}>
                <Box sx={{ px: { xs: 2.5, md: 3.5 }, py: { xs: 2, md: 2.4 } }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 900, color: palette.ink, letterSpacing: '-0.04em', lineHeight: 1.05 }}>
                            Create Generator Fleet Report
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ px: { xs: 2.5, md: 3.5 }, pt: 3, pb: 2.5, backgroundColor: palette.shell }}>
                <Stack spacing={2.25}>
                    <Paper sx={flatSurfaceSx}>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.45fr) minmax(320px, 380px)' },
                                alignItems: 'stretch',
                            }}
                        >
                            <Box
                                sx={{
                                    px: { xs: 1.75, md: 2 },
                                    py: { xs: 1.75, md: 2 },
                                }}
                            >
                                <Box sx={{ pb: 2 }}>
                                    <Stack spacing={1.75}>
                                        <SectionHeader
                                            icon={<CalendarMonthRoundedIcon fontSize="small" />}
                                            title="Report Period"
                                            description="Pick a preset range or fine-tune the timestamps."
                                            tone="emerald"
                                        />

                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: { xs: '1fr', md: '220px minmax(0, 1fr) minmax(0, 1fr)' },
                                                gap: 1.5,
                                            }}
                                        >
                                            <TextField
                                                select
                                                size="small"
                                                fullWidth
                                                label="Quick range"
                                                value={periodType}
                                                onChange={handlePeriodTypeChange}
                                                variant="outlined"
                                                sx={fieldSx}
                                            >
                                                {periodOptions.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>

                                            <DesktopDateTimePicker
                                                autoOk
                                                ampm
                                                disableFuture
                                                value={fromDate}
                                                onChange={handleFromDateChange}
                                                format="Do MMM YY h:mm a"
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        size="small"
                                                        fullWidth
                                                        label="From"
                                                        variant="outlined"
                                                        sx={fieldSx}
                                                    />
                                                )}
                                            />

                                            <DesktopDateTimePicker
                                                autoOk
                                                ampm
                                                disableFuture
                                                value={toDate}
                                                onChange={handleToDateChange}
                                                format="Do MMM YY h:mm a"
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        size="small"
                                                        fullWidth
                                                        label="To"
                                                        variant="outlined"
                                                        error={hasInvalidRange}
                                                        helperText={hasInvalidRange ? 'End time must be after start time.' : ' '}
                                                        sx={fieldSx}
                                                    />
                                                )}
                                            />
                                        </Box>
                                    </Stack>
                                </Box>

                                <Box sx={{ borderTop: `1px solid ${alpha(palette.line, 0.9)}`, pt: 2 }}>
                                    <Stack spacing={1.75}>
                                        <SectionHeader
                                            icon={<DirectionsCarFilledRoundedIcon fontSize="small" />}
                                            title="Vehicles"
                                            description="Choose one or more generator vehicles."
                                            tone="accent"
                                        />

                                        <Autocomplete
                                            size="small"
                                            options={assetOptions}
                                            value={null}
                                            inputValue={vehicleSearch}
                                            onChange={handleVehicleAdd}
                                            onInputChange={(_, newValue) => setVehicleSearch(newValue)}
                                            getOptionDisabled={(option) => selectedAssetIds.includes(option)}
                                            getOptionLabel={(option) => assetNames?.[option]?.regn || option || ''}
                                            renderOption={(props, option) => (
                                                <Box component="li" {...props} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 1.1 }}>
                                                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: palette.ink }}>
                                                        {assetNames?.[option]?.regn || option}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 12, color: palette.muted }}>
                                                        {option}
                                                    </Typography>
                                                </Box>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    label="Search vehicles"
                                                    placeholder="Type a registration number"
                                                    variant="outlined"
                                                    sx={fieldSx}
                                                />
                                            )}
                                        />

                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
                                            <Box>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: palette.ink }}>
                                                    {selectedCount === 0 ? 'No vehicles selected yet' : `${selectedCount} vehicle${selectedCount === 1 ? '' : 's'} selected`}
                                                </Typography>
                                            </Box>
                                            <Button
                                                size="small"
                                                variant={allVehiclesSelected ? 'contained' : 'outlined'}
                                                onClick={handleSelectAllVehicles}
                                                sx={{
                                                    alignSelf: { xs: 'stretch', sm: 'center' },
                                                    borderRadius: '12px',
                                                    px: 1.6,
                                                    py: 0.8,
                                                    textTransform: 'none',
                                                    fontWeight: 800,
                                                    fontSize: 13,
                                                    boxShadow: 'none',
                                                    borderColor: alpha(palette.accent, 0.25),
                                                    color: allVehiclesSelected ? '#ffffff' : palette.accent,
                                                    backgroundColor: allVehiclesSelected ? palette.accent : '#ffffff',
                                                    '&:hover': {
                                                        boxShadow: 'none',
                                                        borderColor: palette.accent,
                                                        backgroundColor: allVehiclesSelected ? '#1d4fd7' : alpha(palette.accent, 0.04),
                                                    },
                                                }}
                                            >
                                                {allVehiclesSelected ? 'Clear selection' : 'Select all vehicles'}
                                            </Button>
                                        </Stack>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 0.75,
                                                minHeight: 32,
                                                padding: selectedCount === 0 ? 1 : 0,
                                                borderRadius: selectedCount === 0 ? '16px' : 0,
                                                backgroundColor: selectedCount === 0 ? alpha(palette.ink, 0.03) : 'transparent',
                                            }}
                                        >
                                            {selectedCount === 0 ? (
                                                <Typography sx={{ fontSize: 12, color: palette.muted }}>
                                                    Selected vehicles will appear here.
                                                </Typography>
                                            ) : (
                                                selectedAssetIds.map((assetId, index) => {
                                                    const label = assetNames?.[assetId]?.regn || multiVehicles[index] || assetId;
                                                    return (
                                                        <Chip
                                                            key={`${assetId}-${index}`}
                                                            label={label}
                                                            onDelete={() => handleVehicleDelete(assetId)}
                                                            size="small"
                                                            sx={{
                                                                height: 30,
                                                                borderRadius: '999px',
                                                                fontWeight: 700,
                                                                fontSize: 12,
                                                                color: palette.accent,
                                                                backgroundColor: alpha(palette.accent, 0.08),
                                                                border: `1px solid ${alpha(palette.accent, 0.16)}`,
                                                                '& .MuiChip-deleteIcon': {
                                                                    color: alpha(palette.accent, 0.8),
                                                                },
                                                            }}
                                                        />
                                                    );
                                                })
                                            )}
                                        </Box>
                                    </Stack>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    px: { xs: 1.75, md: 2 },
                                    py: { xs: 1.75, md: 2 },
                                    borderTop: { xs: `1px solid ${alpha(palette.line, 0.9)}`, lg: 'none' },
                                    borderLeft: { xs: 'none', lg: `1px solid ${alpha(palette.line, 0.9)}` },
                                    background: 'linear-gradient(180deg, #ffffff 0%, #f7fbff 100%)',
                                }}
                            >
                                <Box sx={{ pb: 2 }}>
                                    <Stack spacing={1.5}>
                                        <SectionHeader
                                            icon={<BoltRoundedIcon fontSize="small" />}
                                            title="Report Snapshot"
                                            description="A quick read before you submit."
                                            tone="ink"
                                        />

                                        <Box>
                                            <SummaryRow label="Vehicles" value={`${selectedCount} selected`} />
                                            <SummaryRow label="Range" value={periodLabel} />
                                            <SummaryRow label="Mode" value={forceCrawl ? 'Raw data rebuild' : 'Standard build'} divider={false} />
                                        </Box>
                                    </Stack>
                                </Box>

                                <Box sx={{ borderTop: `1px solid ${alpha(palette.line, 0.9)}`, pt: 2 }}>
                                    <Stack spacing={1.3}>
                                        <Box sx={{ px: 0.15 }}>
                                            <FormControlLabel
                                                sx={{
                                                    margin: 0,
                                                    alignItems: 'flex-start',
                                                    gap: 0.85,
                                                    '& .MuiFormControlLabel-label': {
                                                        flex: 1,
                                                    },
                                                }}
                                                control={(
                                                    <Checkbox
                                                        checked={forceCrawl}
                                                        onChange={(e) => setForceCrawl(e.target.checked)}
                                                        size="small"
                                                        sx={{
                                                            marginTop: 0.05,
                                                            color: palette.amber,
                                                            '&.Mui-checked': {
                                                                color: palette.amber,
                                                            },
                                                        }}
                                                    />
                                                )}
                                                label={(
                                                    <Box sx={{ pt: 0.05 }}>
                                                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: palette.ink }}>
                                                            Recheck Raw Data
                                                        </Typography>
                                                        <Typography sx={{ marginTop: 0.35, fontSize: 12, color: palette.amber, fontWeight: 700 }}>
                                                            Caution: this is a very slow process.
                                                        </Typography>
                                                    </Box>
                                                )}
                                            />
                                        </Box>

                                        <Box sx={{ borderTop: `1px solid ${alpha(palette.line, 0.9)}`, pt: 1.45 }}>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25, marginTop: 1 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Minimum Sensor Activity Time"
                                                    variant="outlined"
                                                    value={minActive}
                                                    onChange={(e) => setMinActive(e.target.value)}
                                                    type="number"
                                                    sx={fieldSx}
                                                    InputProps={{
                                                        endAdornment: <InputAdornment position="end">min</InputAdornment>,
                                                        inputProps: { min: 0, step: 1 },
                                                    }}
                                                />
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Sensor Activity Smoothing"
                                                    variant="outlined"
                                                    value={hold}
                                                    onChange={(e) => setHold(e.target.value)}
                                                    type="number"
                                                    sx={fieldSx}
                                                    InputProps={{
                                                        endAdornment: <InputAdornment position="end">min</InputAdornment>,
                                                        inputProps: { min: 0, step: 1 },
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    </Stack>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>

                    {isAdmin && (
                        <Paper sx={panelSx}>
                            <Stack spacing={1.5}>
                                <SectionHeader
                                    icon={<AdminPanelSettingsRoundedIcon fontSize="small" />}
                                    title="Admin Controls"
                                    description="Advanced toggles for troubleshooting."
                                    tone="ink"
                                />

                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
                                    <ToggleCard
                                        checked={includeEventList}
                                        onChange={setIncludeEventList}
                                        title="Include Event List"
                                        description="Append an event table."
                                    />
                                    <ToggleCard
                                        checked={includeDebug}
                                        onChange={setIncludeDebug}
                                        title="Include Debug"
                                        description="Attach internal debug details."
                                    />
                                    <ToggleCard
                                        checked={disableCrawlFallback}
                                        onChange={setDisableCrawlFallback}
                                        title="Disable Crawl Fallback"
                                        description="Skip the fallback crawl path."
                                    />
                                </Box>
                            </Stack>
                        </Paper>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2.5, md: 3.5 }, py: 1.75, backgroundColor: '#ffffff', borderTop: `1px solid ${palette.line}` }}>
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ width: '100%' }}>
                    <Button
                        onClick={onClose}
                        sx={{
                            px: 2,
                            py: 0.9,
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 800,
                            fontSize: 13,
                            color: palette.text,
                            backgroundColor: alpha(palette.ink, 0.04),
                            '&:hover': {
                                backgroundColor: alpha(palette.ink, 0.08),
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={submitDisabled}
                        sx={{
                            px: 2.3,
                            py: 0.9,
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 800,
                            fontSize: 13,
                            backgroundColor: palette.accent,
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: '#1d4fd7',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        Create Report
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default GenfleetReportForm;
